// @tennis-matches/services/governance — Spalten für Superadmin-Vereinsliste

export const SUPERADMIN_CLUB_LIST_COLUMNS_STORAGE_KEY = "tm_superadmin_clubs_list_columns_v1";

/** @typedef {{ id: string, labelKey: string, defaultVisible?: boolean, locked?: boolean, readOnly?: boolean }} ClubListColumnDef */

/** @type {ClubListColumnDef[]} */
export const SUPERADMIN_CLUB_LIST_COLUMN_DEFS = Object.freeze([
  { id: "clubName", labelKey: "superadmin.clubs.fields.clubName", defaultVisible: true, locked: true },
  { id: "shortName", labelKey: "superadmin.clubs.fields.shortName", defaultVisible: false },
  { id: "legalName", labelKey: "superadmin.clubs.fields.legalName", defaultVisible: false },
  { id: "city", labelKey: "superadmin.clubs.fields.city", defaultVisible: true },
  { id: "postalCode", labelKey: "superadmin.clubs.fields.postalCode", defaultVisible: false },
  { id: "street", labelKey: "superadmin.clubs.fields.street", defaultVisible: false },
  { id: "houseNumber", labelKey: "superadmin.clubs.fields.houseNumber", defaultVisible: false },
  { id: "country", labelKey: "superadmin.clubs.fields.country", defaultVisible: false },
  { id: "email", labelKey: "superadmin.clubs.fields.email", defaultVisible: true },
  { id: "phone", labelKey: "superadmin.clubs.fields.phone", defaultVisible: false },
  { id: "websiteUrl", labelKey: "superadmin.clubs.fields.website", defaultVisible: true },
  { id: "foundedYear", labelKey: "superadmin.clubs.fields.foundedYear", defaultVisible: true },
  { id: "impressumScan", labelKey: "superadmin.clubs.list.col.impressumScan", defaultVisible: true, readOnly: true },
  { id: "registerCourt", labelKey: "superadmin.clubs.fields.registerCourt", defaultVisible: false },
  { id: "registerNumber", labelKey: "superadmin.clubs.fields.registerNumber", defaultVisible: false },
  { id: "dtbClubId", labelKey: "superadmin.clubs.fields.dtbClubId", defaultVisible: false },
  { id: "btvClubId", labelKey: "superadmin.clubs.fields.btvClubId", defaultVisible: false },
  { id: "status", labelKey: "superadmin.clubs.list.col.status", defaultVisible: false, readOnly: true },
  { id: "createdAt", labelKey: "superadmin.clubs.list.col.created", defaultVisible: false, readOnly: true },
  { id: "tenantId", labelKey: "superadmin.clubs.list.col.tenantId", defaultVisible: false, readOnly: true },
]);

const DEF_BY_ID = new Map(SUPERADMIN_CLUB_LIST_COLUMN_DEFS.map((d) => [d.id, d]));

/** @type {Record<string, number>} */
export const SUPERADMIN_CLUB_LIST_COLUMN_STICKY_WIDTHS = Object.freeze({
  clubName: 240,
  impressumScan: 168,
  websiteUrl: 168,
  email: 184,
  city: 128,
  postalCode: 108,
  foundedYear: 96,
  tenantId: 148,
  default: 132,
});

export function getSuperadminClubListColumnStickyWidth(columnId) {
  const id = __asStr(columnId);
  return SUPERADMIN_CLUB_LIST_COLUMN_STICKY_WIDTHS[id] ?? SUPERADMIN_CLUB_LIST_COLUMN_STICKY_WIDTHS.default;
}

export function getDefaultSuperadminClubListPinnedColumnIds() {
  return ["clubName"];
}

const CLUB_FIELD_BY_COLUMN = {
  clubName: "clubName",
  shortName: "shortName",
  legalName: "legalName",
  city: "city",
  postalCode: "postalCode",
  street: "street",
  houseNumber: "houseNumber",
  country: "country",
  email: "email",
  phone: "phone",
  websiteUrl: "websiteUrl",
  foundedYear: "foundedYear",
  registerCourt: "registerCourt",
  registerNumber: "registerNumber",
  dtbClubId: "dtbClubId",
  btvClubId: "btvClubId",
  status: "status",
  createdAt: "createdAt",
  tenantId: "tenantId",
};

function __asStr(v) {
  return String(v ?? "").trim();
}

export function getDefaultSuperadminClubListVisibleColumnIds() {
  return SUPERADMIN_CLUB_LIST_COLUMN_DEFS.filter((d) => d.defaultVisible !== false || d.locked).map((d) => d.id);
}

function canUseStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function loadSuperadminClubListVisibleColumnIds() {
  return loadSuperadminClubListColumnLayout().visible;
}

export function loadSuperadminClubListPinnedColumnIds() {
  return loadSuperadminClubListColumnLayout().pinned;
}

/**
 * @returns {{ visible: string[], pinned: string[] }}
 */
export function loadSuperadminClubListColumnLayout() {
  const visibleDefaults = getDefaultSuperadminClubListVisibleColumnIds();
  const pinnedDefaults = getDefaultSuperadminClubListPinnedColumnIds();
  if (!canUseStorage()) {
    return { visible: visibleDefaults, pinned: pinnedDefaults };
  }
  try {
    const raw = window.localStorage.getItem(SUPERADMIN_CLUB_LIST_COLUMNS_STORAGE_KEY);
    if (!raw) return { visible: visibleDefaults, pinned: pinnedDefaults };
    const parsed = JSON.parse(raw);
    const visibleRaw = Array.isArray(parsed?.visible) ? parsed.visible.map((x) => __asStr(x)).filter(Boolean) : [];
    const pinnedRaw = Array.isArray(parsed?.pinned) ? parsed.pinned.map((x) => __asStr(x)).filter(Boolean) : [];
    const visible = visibleRaw.filter((id) => DEF_BY_ID.has(id));
    const pinned = pinnedRaw.filter((id) => DEF_BY_ID.has(id));
    if (!visible.includes("clubName")) visible.unshift("clubName");
    return {
      visible: visible.length ? visible : visibleDefaults,
      pinned: normalizeSuperadminClubListPinnedColumnIds(
        pinned.length ? pinned : pinnedDefaults,
        visible.length ? visible : visibleDefaults
      ),
    };
  } catch {
    return { visible: visibleDefaults, pinned: pinnedDefaults };
  }
}

export function normalizeSuperadminClubListPinnedColumnIds(pinnedIds, visibleIds) {
  const visible = new Set(Array.isArray(visibleIds) ? visibleIds : []);
  return (Array.isArray(pinnedIds) ? pinnedIds : [])
    .map((x) => __asStr(x))
    .filter((id) => visible.has(id) && DEF_BY_ID.has(id));
}

export function saveSuperadminClubListVisibleColumnIds(visibleIds) {
  const layout = loadSuperadminClubListColumnLayout();
  saveSuperadminClubListColumnLayout(visibleIds, layout.pinned);
}

export function saveSuperadminClubListColumnLayout(visibleIds, pinnedIds) {
  if (!canUseStorage()) return;
  const visible = (Array.isArray(visibleIds) ? visibleIds : [])
    .map((x) => __asStr(x))
    .filter((id) => DEF_BY_ID.has(id));
  if (!visible.includes("clubName")) visible.unshift("clubName");
  const pinned = normalizeSuperadminClubListPinnedColumnIds(pinnedIds, visible);
  try {
    window.localStorage.setItem(
      SUPERADMIN_CLUB_LIST_COLUMNS_STORAGE_KEY,
      JSON.stringify({
        visible: visible.length ? visible : getDefaultSuperadminClubListVisibleColumnIds(),
        pinned: pinned.length ? pinned : getDefaultSuperadminClubListPinnedColumnIds(),
      })
    );
  } catch {
    // noop
  }
}

export function resolveSuperadminClubListVisibleColumns(visibleIds) {
  const ids = Array.isArray(visibleIds) ? visibleIds : getDefaultSuperadminClubListVisibleColumnIds();
  const seen = new Set();
  /** @type {ClubListColumnDef[]} */
  const ordered = [];
  for (const id of ids) {
    if (!DEF_BY_ID.has(id) || seen.has(id)) continue;
    seen.add(id);
    ordered.push(DEF_BY_ID.get(id));
  }
  if (!seen.has("clubName")) ordered.unshift(DEF_BY_ID.get("clubName"));
  return ordered;
}

/**
 * @param {ClubListColumnDef[]} columns
 * @param {string[]} pinnedIds
 * @returns {Record<string, number>}
 */
export function computeSuperadminClubListStickyLeftOffsets(columns, pinnedIds) {
  const pinned = new Set(normalizeSuperadminClubListPinnedColumnIds(pinnedIds, columns.map((c) => c.id)));
  /** @type {Record<string, number>} */
  const offsets = {};
  let left = 0;
  for (const col of columns) {
    if (!pinned.has(col.id)) continue;
    offsets[col.id] = left;
    left += getSuperadminClubListColumnStickyWidth(col.id);
  }
  return offsets;
}

/**
 * @param {string} columnId
 * @param {object|null|undefined} club
 * @param {{ read?: (k: string) => string, emptyLabel?: string }} [ctx]
 */
export function formatSuperadminClubListCellValue(columnId, club, ctx = {}) {
  const empty = __asStr(ctx?.emptyLabel) || "—";
  const c = club && typeof club === "object" ? club : {};
  const field = CLUB_FIELD_BY_COLUMN[columnId] || columnId;
  const raw = c[field];

  if (columnId === "createdAt") {
    const v = __asStr(raw);
    if (!v) return empty;
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return v;
      return d.toLocaleString("de-DE");
    } catch {
      return v;
    }
  }

  if (columnId === "foundedYear") {
    const y = raw != null && raw !== "" ? String(raw) : "";
    return y || empty;
  }

  if (columnId === "country") {
    const v = __asStr(raw) || "DE";
    return v || empty;
  }

  const val = __asStr(raw);
  return val || empty;
}

export function isSuperadminClubListCellEmpty(columnId, club, ctx) {
  const val = formatSuperadminClubListCellValue(columnId, club, ctx);
  const empty = __asStr(ctx?.emptyLabel) || "—";
  return !val || val === empty;
}

export function getSuperadminClubListColumnDef(columnId) {
  return DEF_BY_ID.get(columnId) || null;
}
